import { CrmTaskStatus } from '@plato/data/prisma';
import { endOfDay } from 'date-fns';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppState } from '../../../lib/hooks/use-app-state.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmTaskFilterOptions } from '../../../server/dto/crm-task-dto.ts';
import { api } from '../../../utils/api.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import type { MenuGroup } from '../contracts.ts';
import { NavPathOverrideEnum } from '../contracts.ts';
import { buildManagerNavigation } from '../utils/build-manager-navigation.ts';
import { buildRepNavigation } from '../utils/build-rep-navigation.ts';
import { useIsManagerView } from './use-view-mode.ts';

export const usePlatoLayoutNavigation = () => {
    const { platoUser } = useAppState();
    const { t } = useLocale();
    const location = useLocation();

    const isManagerView = useIsManagerView();
    const appConfig = useAppConfig();

    const { data: taskCount } = api.platoUser.query.tasks.getManyTasks.count.useQuery(
        {
            customerIds: [],
            assignedToIds: [platoUser.id],
            assignedByIds: [],
            status: CrmTaskStatus.OPEN,
            dueDate: [
                {
                    direction: 'LT',
                    timestamp: endOfDay(new Date()).toISOString(),
                },
            ],
        } satisfies CrmTaskFilterOptions,
        {
            refetchInterval: 60 * 1000,
        },
    );

    const navigation: MenuGroup[] = useMemo(() => {
        const buildOptions = { t, taskCount, appConfig };
        return isManagerView ? buildManagerNavigation(buildOptions) : buildRepNavigation(buildOptions);
    }, [t, taskCount, appConfig, isManagerView]);

    const activeSection = useMemo(() => {
        // Special case: /integrations/tapi routes should highlight Customers
        if (location.pathname.startsWith('/integrations/tapi')) {
            return NavPathOverrideEnum.CUSTOMERS;
        }

        const items = navigation.flatMap((group) => group.items);

        for (const item of items) {
            if (item.subItems) {
                for (const subItem of item.subItems) {
                    if (location.pathname.startsWith(subItem.href)) {
                        return subItem.id;
                    }
                }
            }
        }

        for (const item of items) {
            if (item.href && location.pathname.startsWith(item.href)) {
                return item.id;
            }
        }

        return NavPathOverrideEnum.HOME;
    }, [navigation, location.pathname]);

    return { navigation, activeSection };
};
